// Point d'entrée UNIQUE pour tous les appels réseau vers le vrai backend StoreFlowProV2
// (proxifié via /api/* par server/index.js — voir le plan de migration, Phase 0). Reprend
// volontairement le même comportement que apiJSON() côté vanilla (public/modules/shared.js)
// pour que les deux frontends se comportent de façon cohérente contre la même API :
// cache-busting sur chaque requête (évite un GET périmé une fois qu'un service worker
// existera côté React aussi), même forme d'erreur ({error, details}) relayée telle quelle.

// Faille de sécurité réelle corrigée (retour utilisateur 2026-08-10) : jusqu'ici, un 401
// en cours de session (cookie expiré pendant que l'app reste ouverte) n'était géré QUE
// localement, écran par écran (chaque useEffect fait son propre .catch(() => {})) —
// aucun endroit ne remettait l'app dans un état de connexion cohérent, donc les écrans
// restaient affichés indéfiniment sans jamais recharger la moindre donnée ("app à moitié
// connectée"). onUnauthorized est enregistré UNE FOIS par App.jsx (voir l'effet de
// revalidation de session) et appelé ici pour TOUT 401, quel que soit l'appel — un seul
// point de vérité, jamais besoin de dupliquer la logique dans chaque écran.
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) { onUnauthorized = fn; }

// Délai avant abandon d'une requête bloquée (retour utilisateur explicite, 2026-08-20,
// captures d'écran "Wird geladen..." sans fin sur Pilotage réseau > Comparaison) : un
// fetch() qui ne se termine JAMAIS (connexion mobile qui se fige en plein transfert,
// bascule Wi-Fi/4G en cours de requête — jamais un vrai timeout réseau qui rejetterait
// proprement) laissait certains écrans bloqués en chargement pour toujours, sans recours
// autre que fermer l'app. Un `.catch(() => [])` sur un appel qui ne se règle NI ne rejette
// jamais ne sert à rien : il n'y a rien à catcher tant que la promesse ne se termine pas.
// 25s : assez généreux pour un Promise.all de plusieurs mois × plusieurs familles sur une
// connexion 4G lente (déjà vu dépasser 10s en réel), mais jamais indéfini.
const REQUEST_TIMEOUT_MS = 25000;

export async function apiJSON(url, options = {}) {
  const { errorFallback, ...fetchOptions } = options;
  const withBuster = url + (url.includes("?") ? "&" : "?") + "_=" + Date.now();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res;
  try {
    res = await fetch(withBuster, {
      cache: "no-store",
      credentials: "include",
      headers: fetchOptions.body instanceof FormData ? {} : { "Content-Type": "application/json" },
      ...fetchOptions,
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === "AbortError") {
      const timeoutErr = new Error("Délai de connexion dépassé — vérifie ta connexion et réessaie.");
      timeoutErr.status = 0;
      timeoutErr.isTimeout = true;
      throw timeoutErr;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  const data = await res.json().catch(() => ({}));

  // offlineOutbox.js (installé dans main.jsx, avant le premier rendu de App) répond 202
  // {queued:true} — jamais un échec HTTP — quand fetch() a rejeté pour une vraie coupure
  // réseau. Chokepoint unique oblige (voir en-tête de ce fichier) : plutôt que de faire
  // vérifier isQueuedResult() à chacun des 343 appelants d'écriture (dont 9+ dans Rapport de
  // filiale qui font setFrDraft(data.report)/setFrDetail(data.report) sans garde et
  // planteraient sur un data.report absent), on lève ici une erreur au message déjà correct
  // — chaque appelant a DÉJÀ un .catch(err => showToast(err.message || fallback)), donc les
  // 343 sites deviennent sûrs d'un coup, sans y toucher. showToast() ne porte aucun style
  // d'erreur (App.jsx:4625, juste un texte) : ce message s'affiche exactement comme un toast
  // de succès normal, pas comme une alerte.
  if (data && data.queued === true) {
    const queuedErr = new Error("Hors-ligne — action enregistrée, sera synchronisée dès la reconnexion.");
    queuedErr.queued = true;
    queuedErr.outboxId = data.outboxId;
    throw queuedErr;
  }

  if (!res.ok) {
    const fallback = errorFallback || "Erreur serveur";
    const detail = data.details && typeof data.details === "string" ? ` (${data.details})` : "";
    const err = new Error((data.error || data.message || fallback) + detail);
    err.status = res.status;
    err.data = data;
    if (res.status === 401 && onUnauthorized) onUnauthorized(url);
    throw err;
  }
  return data;
}
